import { ChevronLeft, FileAudio, Loader2, Pause, Play } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";
import {
  InputLanguageChip,
  NoteLanguageChip,
  VisitTitleHeading,
} from "./NewVisitSetupV32";
import { useRecordingSetup } from "./useRecordingSetup";

const clock = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const size = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Play the chosen file before sending it: play/pause, position, time. */
export const FilePlayer = ({ url }: { url: string }): JSX.Element => {
  const { t } = useTranslation();
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const element = audio.current;
    return () => element?.pause();
  }, []);
  return (
    <div className="flex items-center gap-3">
      {/* biome-ignore lint/a11y/useMediaCaption: a patient recording has no captions; the note is written from it. */}
      <audio
        ref={audio}
        preload="metadata"
        src={url}
        onDurationChange={(event) => {
          const value = event.currentTarget.duration;
          if (Number.isFinite(value)) setDuration(value);
        }}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
      />
      <V3IconButton
        className="bg-v3-surface-2 shadow-v3-hairline"
        label={
          playing ? t("recorderV32.pause") : t("newVisitV32.uploadReview.play")
        }
        onClick={() => {
          const element = audio.current;
          if (!element) return;
          if (element.paused) void element.play().catch(() => undefined);
          else element.pause();
        }}
      >
        {playing ? (
          <Pause className="fill-current" />
        ) : (
          <Play className="fill-current" />
        )}
      </V3IconButton>
      <input
        aria-label={t("newVisitV32.uploadReview.position")}
        className="h-1 min-w-0 flex-1 cursor-pointer accent-[var(--v3-ink)]"
        max={duration || 0}
        min={0}
        step={0.1}
        type="range"
        value={Math.min(time, duration || 0)}
        onChange={(event) => {
          const element = audio.current;
          if (element) element.currentTime = Number(event.target.value);
        }}
      />
      <span className="shrink-0 font-mono text-[13px] tabular-nums text-v3-text-2">
        {clock(time)} / {clock(duration)}
      </span>
    </div>
  );
};

/**
 * v3.2: a chosen recording before it is sent. The file with a player, the
 * conversation and note language and the template, then "Create note". The
 * upload then runs as before (queued, the visit appears in the list).
 */
export const NewVisitUploadV32 = ({
  file,
  url,
  templateChip,
  busy,
  onSubmit,
  onRequestClose,
  visitTitle,
  onVisitTitleChange,
  children,
  badge,
}: {
  /** The optional visit title, typed in the heading. */
  visitTitle?: string;
  onVisitTitleChange?: (title: string) => void;
  file: File;
  url: string;
  templateChip?: ReactNode;
  busy: boolean;
  onSubmit: () => void;
  /** Back: asks before discarding the chosen file. */
  onRequestClose: () => void;
  /** Errors from the upload (and, for a practice consulta, its script). */
  children?: ReactNode;
  /** Beside the screen title (the "Practice" label). */
  badge?: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const setup = useRecordingSetup();
  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col self-stretch bg-v3-panel font-v3 text-v3-text"
      data-testid="newvisit-upload-v32"
    >
      <header className="flex h-14 shrink-0 items-center gap-1 px-1.5">
        <V3IconButton
          disabled={busy}
          label={t("recorderV32.back")}
          onClick={onRequestClose}
        >
          <ChevronLeft className="!size-5" />
        </V3IconButton>
        <p className="text-[15px] font-semibold text-v3-text-2">
          {t("newVisitV32.upload")}
        </p>
        {badge && <span className="ml-1.5">{badge}</span>}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-2">
        <VisitTitleHeading
          placeholder={t("newVisitV32.title")}
          value={visitTitle}
          onChange={onVisitTitleChange}
        />
        <div className="mt-6 rounded-v3-panel bg-v3-panel p-4 shadow-v3-hairline-strong">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-v3-surface-2 text-v3-text-2"
            >
              <FileAudio className="size-5" />
            </span>
            <div className="min-w-0">
              <p
                className="truncate text-[15px] font-semibold"
                data-testid="newvisit-upload-file"
              >
                {file.name}
              </p>
              <p className="text-[13px] text-v3-text-3">{size(file.size)}</p>
            </div>
          </div>
          <div className="mt-3">
            <FilePlayer url={url} />
          </div>
        </div>
        <p className="mt-4 text-[14px] leading-5 text-v3-text-3">
          {t("newVisitV32.uploadReview.hint")}
        </p>
        {children && <div className="mt-6 space-y-3">{children}</div>}
      </div>
      <div className="shrink-0 space-y-3 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-2 sm:px-4 sm:pb-4">
        <fieldset className="flex min-w-0 flex-wrap items-start gap-2 border-0 px-1">
          <legend className="sr-only">{t("newVisitV32.options")}</legend>
          <InputLanguageChip setup={setup} />
          <NoteLanguageChip inputLanguage={setup.inputLanguageValue} />
          {templateChip}
        </fieldset>
        <V3Button
          className="h-[52px] w-full text-[16px]"
          data-testid="newvisit-upload-submit"
          disabled={busy}
          variant="primary"
          onClick={onSubmit}
        >
          {busy && <Loader2 className="animate-spin" />}
          {busy
            ? t("visits.sidebar.processing")
            : t("newVisitV32.uploadReview.create")}
        </V3Button>
      </div>
    </div>
  );
};
